import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { fetchQuizResult, waitForQuizResultWithTimeout } from '../services/quizResultService';
import { getSessionMetadata } from '../services/sessionService';
import { mapPersonalityToCard } from '../utils/tarotMapper';
import { analyticsService } from '../services/analyticsService';
import TarotCard from './TarotCard';
import ResultsLoading from './ResultsLoading';
import './QuizPage.css';

/**
 * ResultsPage - Display quiz results from deep link
 *
 * Route: /results/:sessionId
 *
 * Flow:
 * 1. Check if session exists and quiz is completed
 * 2. If not completed → redirect to /
 * 3. If completed but no results yet → show ResultsLoading
 * 4. If results exist → show TarotCard
 *
 * Used when users click "View Full Results" from email,
 * or redirected here after submitting email on QuizPage.
 */
const ResultsPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [cardData, setCardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [waitingForResults, setWaitingForResults] = useState(false);
  const [error, setError] = useState(null);
  const cleanupRef = useRef(null);
  const hasTrackedResultsView = useRef(false);

  // Track results viewed when cardData is loaded
  useEffect(() => {
    if (cardData && !hasTrackedResultsView.current) {
      hasTrackedResultsView.current = true;
      analyticsService.trackResultsViewed({
        sessionId,
        personalityType: cardData.title || 'unknown',
      });
    }
  }, [cardData, sessionId]);

  // Handle results arriving from Firestore listener
  const handleResultsReady = useCallback((result) => {
    console.log('✅ Results ready:', result);

    const mappedCard = mapPersonalityToCard(
      result.personality_type,
      result.summary,
      result.summary_short
    );

    if (!mappedCard) {
      setError('Unable to load personality card');
      setWaitingForResults(false);
      return;
    }

    console.log('🃏 Card data loaded:', mappedCard);
    setCardData(mappedCard);
    setWaitingForResults(false);
  }, []);

  // Handle timeout while waiting for results
  const handleTimeout = useCallback(() => {
    console.log('⏰ Results timeout');
    setError('Results are taking longer than expected. Please check your email for results.');
    setWaitingForResults(false);
  }, []);

  useEffect(() => {
    const loadResults = async () => {
      if (!sessionId) {
        setError('No session ID provided');
        setLoading(false);
        return;
      }

      try {
        console.log('📱 Loading results for session:', sessionId);

        // Step 1: Check if session exists and quiz is completed
        const sessionMetadata = await getSessionMetadata(sessionId);

        if (!sessionMetadata) {
          console.log('❌ Session not found, redirecting to home');
          navigate('/', { replace: true });
          return;
        }

        // Check if quiz is completed
        if (!sessionMetadata.metadata?.quiz_completed) {
          console.log('❌ Quiz not completed, redirecting to home');
          navigate('/', { replace: true });
          return;
        }

        console.log('✅ Quiz completed, checking for results...');

        // Step 2: Try to fetch existing results
        const result = await fetchQuizResult(sessionId);

        if (result) {
          // Results exist - show TarotCard
          const mappedCard = mapPersonalityToCard(
            result.personality_type,
            result.summary,
            result.summary_short
          );

          if (!mappedCard) {
            setError('Unable to load personality card');
            setLoading(false);
            return;
          }

          console.log('🃏 Card data loaded:', mappedCard);
          setCardData(mappedCard);
          setLoading(false);
        } else {
          // No results yet - show ResultsLoading and start listening
          console.log('📭 No results yet, showing loading and listening...');
          setLoading(false);
          setWaitingForResults(true);

          // Start listening for results with a 65 second timeout
          cleanupRef.current = waitForQuizResultWithTimeout(
            sessionId,
            handleResultsReady,
            handleTimeout,
            (err) => {
              console.error('❌ Error listening for results:', err);
              setError('Failed to load results. Please try again.');
              setWaitingForResults(false);
            },
            65000, // 65 second timeout to match ResultsLoading
            true   // Keep listening after timeout
          );
        }
      } catch (err) {
        console.error('❌ Error loading results:', err);
        setError('Failed to load results. Please try again.');
        setLoading(false);
      }
    };

    loadResults();

    // Cleanup listener on unmount
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
      }
    };
  }, [sessionId, navigate, handleResultsReady, handleTimeout]);

  // Initial loading state (checking session metadata)
  if (loading || authLoading) {
    return (
      <div className="results-page-container">
        <div style={styles.loadingContainer}>
          <img
            src="/assets/images/jaimee-logo-cream.svg"
            alt="Jaimee"
            style={styles.logo}
          />
          <div style={styles.loadingText}>Loading your results...</div>
          <div style={styles.spinner}></div>
        </div>
      </div>
    );
  }

  // Waiting for results from backend (show ResultsLoading component)
  if (waitingForResults) {
    return (
      <div className="results-page-container">
        <ResultsLoading
          sessionId={sessionId}
          onResultsReady={handleResultsReady}
          onTimeout={handleTimeout}
        />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="results-page-container">
        <div style={styles.errorContainer}>
          <img
            src="/assets/images/jaimee-logo-cream.svg"
            alt="Jaimee"
            style={styles.logo}
          />
          <div style={styles.errorIcon}>😕</div>
          <h2 style={styles.errorTitle}>Oops!</h2>
          <p style={styles.errorText}>{error}</p>
          <Link to="/" style={styles.ctaButton}>
            Take the Quiz
          </Link>
        </div>
      </div>
    );
  }

  // Results display
  return (
    <div className="results-page-container">
      <TarotCard
        cardData={cardData}
        userEmail={null}
        user={user}
      />
    </div>
  );
};

const styles = {
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    padding: '20px',
    background: '#2E2C28',
  },
  logo: {
    width: '120px',
    marginBottom: '30px',
  },
  loadingText: {
    color: '#F5F1E8',
    fontFamily: 'Manrope, sans-serif',
    fontSize: '18px',
    marginBottom: '20px',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '3px solid rgba(245, 241, 232, 0.3)',
    borderTop: '3px solid #FAA21B',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    padding: '20px',
    textAlign: 'center',
    background: '#2E2C28',
  },
  errorIcon: {
    fontSize: '60px',
    marginBottom: '20px',
  },
  errorTitle: {
    color: '#F5F1E8',
    fontFamily: 'Manrope, sans-serif',
    fontSize: '28px',
    fontWeight: '700',
    marginBottom: '15px',
  },
  errorText: {
    color: '#E5DFD0',
    fontFamily: 'Manrope, sans-serif',
    fontSize: '16px',
    marginBottom: '30px',
    maxWidth: '300px',
    lineHeight: '1.5',
  },
  ctaButton: {
    display: 'inline-block',
    backgroundColor: '#FAA21B',
    color: '#2C2C2C',
    textDecoration: 'none',
    padding: '16px 40px',
    borderRadius: '30px',
    fontSize: '16px',
    fontWeight: '700',
    fontFamily: 'Manrope, sans-serif',
  },
};

export default ResultsPage;
